import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect, MultiSelectOption } from "@/components/ui/multi-select";
import { Search, CalendarIcon, X, Filter, Check } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export interface TransactionFilters {
  search: string;
  categoryId: string | null;
  accountIds: string[];
  accountType: "all" | "checking" | "credit_card" | "savings" | "goals";
  types: string[];
  startDate: Date | null;
  endDate: Date | null;
  minAmount: string;
  maxAmount: string;
  isPaid: "all" | "paid" | "pending";
}

interface Category {
  id: string;
  name: string;
  color: string;
  type: string;
}

interface Account {
  id: string;
  name: string;
  color: string;
  type: string;
}

interface TransactionFiltersProps {
  filters: TransactionFilters;
  onFiltersChange: (filters: TransactionFilters) => void;
  onClear: () => void;
}

const TYPE_OPTIONS: MultiSelectOption[] = [
  { value: "income", label: "Receitas" },
  { value: "expense", label: "Despesas" },
  { value: "transfer_out", label: "Transferência (saída)" },
  { value: "transfer_in", label: "Transferência (entrada)" },
];

export const TransactionFiltersComponent = ({
  filters,
  onFiltersChange,
  onClear,
}: TransactionFiltersProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const hasAdvancedFilters =
    filters.accountType !== "all" ||
    filters.types.length > 0 ||
    filters.categoryId !== null ||
    filters.isPaid !== defaultFilters.isPaid ||
    filters.startDate !== null ||
    filters.endDate !== null ||
    filters.minAmount !== "" ||
    filters.maxAmount !== "";
  const [isExpanded, setIsExpanded] = useState(hasAdvancedFilters);
  const [localFilters, setLocalFilters] = useState<TransactionFilters>(filters);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchAccounts();
  }, []);

  // Sync local state when external filters change (e.g., on clear)
  useEffect(() => {
    setLocalFilters(filters);
    setHasLocalChanges(false);
  }, [filters]);

  const fetchCategories = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from("categories")
        .select("id, name, color, type")
        .or(`user_id.eq.${user.id},is_system.eq.true`)
        .order("name");
      setCategories(data || []);
    }
  };

  const fetchAccounts = async () => {
    const { data } = await supabase
      .from("accounts")
      .select("id, name, color, type")
      .eq("is_active", true)
      .eq("archived", false)
      .order("name");
    setAccounts(data || []);
  };

  const updateLocalFilter = <K extends keyof TransactionFilters>(
    key: K,
    value: TransactionFilters[K]
  ) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
    setHasLocalChanges(true);
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
    setHasLocalChanges(false);
  };

  // Immediate filters: search and accounts apply without the button
  const updateImmediateFilter = <K extends keyof TransactionFilters>(
    key: K,
    value: TransactionFilters[K]
  ) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
    onFiltersChange(updated);
    setHasLocalChanges(false);
  };

  const handleClear = () => {
    setHasLocalChanges(false);
    onClear();
  };

  const hasActiveFilters =
    filters.search ||
    filters.categoryId ||
    filters.accountIds.length > 0 ||
    filters.types.length > 0 ||
    filters.startDate ||
    filters.endDate ||
    filters.minAmount ||
    filters.maxAmount ||
    filters.isPaid !== "all";

  const activeFilterCount = [
    filters.search,
    filters.categoryId,
    filters.accountIds.length > 0,
    filters.accountType !== "all",
    filters.types.length > 0,
    filters.startDate || filters.endDate,
    filters.minAmount || filters.maxAmount,
    filters.isPaid !== "all",
  ].filter(Boolean).length;

  const accountOptions: MultiSelectOption[] = accounts.map((acc) => ({
    value: acc.id,
    label: acc.name,
    color: acc.color,
  }));

  return (
    <div className="space-y-3">
      {/* Account filter and Search bar - always visible, apply immediately */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="w-full sm:w-48 md:w-64">
          <MultiSelect
            options={accountOptions}
            selected={localFilters.accountIds}
            onChange={(selected) => updateImmediateFilter("accountIds", selected)}
            placeholder="Todas as contas"
            emptyMessage="Nenhuma conta encontrada"
          />
        </div>
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por descrição..."
              value={localFilters.search}
              onChange={(e) => updateImmediateFilter("search", e.target.value)}
              className="pl-9"
            />
          </div>
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="icon" className="relative shrink-0">
                <Filter className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
          {hasActiveFilters && (
            <Button variant="ghost" size="icon" onClick={handleClear} className="shrink-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Expandable filters - require Apply button */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
            {/* Account type filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Tipo de conta</Label>
              <Select
                value={localFilters.accountType}
                onValueChange={(value) => updateLocalFilter("accountType", value as TransactionFilters["accountType"])}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as contas</SelectItem>
                  <SelectItem value="checking">Conta Corrente</SelectItem>
                  <SelectItem value="credit_card">Cartão de Crédito</SelectItem>
                  <SelectItem value="savings">Poupança</SelectItem>
                  <SelectItem value="goals">Cofrinhos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type filter - multi-select */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Tipo</Label>
              <MultiSelect
                options={TYPE_OPTIONS}
                selected={localFilters.types}
                onChange={(selected) => updateLocalFilter("types", selected)}
                placeholder="Todos os tipos"
                emptyMessage="Nenhum tipo encontrado"
              />
            </div>

            {/* Category filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Categoria</Label>
              <Select
                value={localFilters.categoryId || "all"}
                onValueChange={(value) => updateLocalFilter("categoryId", value === "all" ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select
                value={localFilters.isPaid}
                onValueChange={(value) => updateLocalFilter("isPaid", value as TransactionFilters["isPaid"])}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="paid">Efetivados</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date range - Start */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Período</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full sm:flex-1 justify-start text-left font-normal",
                        !localFilters.startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {localFilters.startDate ? format(localFilters.startDate, "dd/MM/yy") : "Início"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={localFilters.startDate || undefined}
                      onSelect={(date) => updateLocalFilter("startDate", date || null)}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full sm:flex-1 justify-start text-left font-normal",
                        !localFilters.endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {localFilters.endDate ? format(localFilters.endDate, "dd/MM/yy") : "Fim"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={localFilters.endDate || undefined}
                      onSelect={(date) => updateLocalFilter("endDate", date || null)}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Amount range */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Valor (R$)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="number"
                  placeholder="Mínimo"
                  value={localFilters.minAmount}
                  onChange={(e) => updateLocalFilter("minAmount", e.target.value)}
                  className="w-full sm:flex-1"
                />
                <Input
                  type="number"
                  placeholder="Máximo"
                  value={localFilters.maxAmount}
                  onChange={(e) => updateLocalFilter("maxAmount", e.target.value)}
                  className="w-full sm:flex-1"
                />
              </div>
            </div>

            {/* Apply button */}
            <div className="flex items-end sm:col-span-2 lg:col-span-2">
              <Button
                onClick={applyFilters}
                className="w-full sm:w-auto"
                disabled={!hasLocalChanges}
              >
                <Check className="mr-2 h-4 w-4" />
                Aplicar filtros
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export const defaultFilters: TransactionFilters = {
  search: "",
  categoryId: null,
  accountIds: [],
  accountType: "all",
  types: [],
  startDate: null,
  endDate: null,
  minAmount: "",
  maxAmount: "",
  isPaid: "paid",
};
